import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import {
  Patient,
  PatientAPI,
  PatientList,
  PatientListAPI,
} from '../models/patient';
import { map, Observable, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  DemandeContactList,
  DemandeContactListAPI,
} from '../models/demandeContact';
import { DetailTelesuivi, DetailTelesuiviAPI } from '../models/detailTelesuivi';
import { InterventionList, InterventionListAPI } from '../models/intervention';
import { Telesuivi, TelesuiviAPI } from '../models/telesuivi';
import { PrescriptionList, PrescriptionListAPI } from '../models/prescription';
import { UserList, UserListAPI } from '../models/user';
import {
  PatientComplement,
  PatientComplementAPI,
} from '../models/patientComplement';

@Injectable({
  providedIn: 'root',
})
export class PatientService {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);

  getHeaders(): HttpHeaders {
    return new HttpHeaders({
      Authorization: 'Bearer ' + this.authService.getToken(),
    });
  }

  pullList(
    page: number | undefined = undefined,
    nom: string | undefined,
    dosActiviteList: string[] = [],
    dosEcheanceMin: Date | undefined = undefined,
    dosEcheanceMax: Date | undefined = undefined,
    dosEcheanceInitiales: boolean = false,
  ): Observable<PatientList> {
    const headers = this.getHeaders();

    var params: HttpParams = new HttpParams();

    page ? (params = params.set('page', page)) : null;

    params = params.set('per_page', 15);

    nom ? (params = params.set('q', nom)) : null;

    for (let index = 0; index < dosActiviteList.length; index++) {
      const dosActivite = dosActiviteList[index];

      params = params.set('dos_activite[' + index + ']', dosActivite);
    }

    if (dosEcheanceMin) {
      const year = dosEcheanceMin.getFullYear();
      const month = String(dosEcheanceMin.getMonth() + 1).padStart(2, '0');
      const day = String(dosEcheanceMin.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;

      params = params.set('date_echeance_min', formattedDate);
    }

    if (dosEcheanceMax) {
      const year = dosEcheanceMax.getFullYear();
      const month = String(dosEcheanceMax.getMonth() + 1).padStart(2, '0');
      const day = String(dosEcheanceMax.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;

      params = params.set('date_echeance_max', formattedDate);
    }

    dosEcheanceInitiales ? (params = params.set('echeance_initiale', 1)) : null;

    return this.http
      .get<PatientListAPI>(`${environment.api.gateway}patients`, {
        headers: headers,
        params: params,
      })
      .pipe(
        map((patientListAPI: PatientListAPI) =>
          PatientList.factory(patientListAPI),
        ),
      );
  }

  pullDetail(id: number): Observable<Patient> {
    const headers = this.getHeaders();

    return this.http
      .get<PatientAPI>(`${environment.api.gateway}patients/${id}`, {
        headers,
      })
      .pipe(map((patient: PatientAPI) => Patient.factory(patient)));
  }

  pullComplement(patient: Patient): Observable<PatientComplement> {
    const headers = this.getHeaders();

    return this.http
      .get<PatientComplementAPI>(
        `${environment.api.gateway}patients/${patient.id}/complement?`,
        {
          headers,
        },
      )
      .pipe(
        map((patientComplement: PatientComplementAPI) =>
          PatientComplement.factory(patientComplement),
        ),
      );
  }

  pullTelesuivi(patient: Patient): Observable<Telesuivi> {
    const headers = this.getHeaders();

    return this.http
      .get<TelesuiviAPI>(
        `${environment.api.gateway}patients/${patient.id}/telesuivi`,
        {
          headers,
        },
      )
      .pipe(
        map((telesuiviAPI: TelesuiviAPI) => Telesuivi.factory(telesuiviAPI)),
      );
  }

  pullDetailTelesuivi(patient: Patient): Observable<DetailTelesuivi> {
    const headers = this.getHeaders();

    return this.http
      .get<DetailTelesuiviAPI>(
        `${environment.api.gateway}patients/${patient.id}/detail-telesuivi`,
        {
          headers,
        },
      )
      .pipe(
        map((detailTelesuiviAPI: DetailTelesuiviAPI) =>
          DetailTelesuivi.factory(detailTelesuiviAPI),
        ),
      );
  }

  pullPrescriptionList(patient: Patient): Observable<PrescriptionList> {
    const headers = this.getHeaders();

    return this.http
      .get<PrescriptionListAPI>(
        `${environment.api.gateway}patients/${patient.id}/prescription`,
        {
          headers,
        },
      )
      .pipe(
        map((prescriptionListAPI: PrescriptionListAPI) =>
          PrescriptionList.factory(prescriptionListAPI),
        ),
      );
  }

  pullInterventionList(patient: Patient): Observable<InterventionList> {
    const headers = this.getHeaders();

    return this.http
      .get<InterventionListAPI>(
        `${environment.api.gateway}patients/${patient.id}/interventions`,
        {
          headers,
        },
      )
      .pipe(
        map((interventionListAPI: InterventionListAPI) =>
          InterventionList.factory(interventionListAPI),
        ),
      );
  }

  pullDemandeContactList(patient: Patient): Observable<DemandeContactList> {
    const headers = this.getHeaders();

    return this.http
      .get<DemandeContactListAPI>(
        `${environment.api.gateway}patients/${patient.id}/demandes-contact`,
        {
          headers: headers,
        },
      )
      .pipe(
        map((demandeContactListAPI: DemandeContactListAPI) =>
          DemandeContactList.factory(demandeContactListAPI),
        ),
      );
  }

  pullUserList(patient: Patient): Observable<UserList> {
    const headers = this.getHeaders();

    return this.http
      .get<UserListAPI>(
        `${environment.api.gateway}patients/${patient.id}/users`,
        {
          headers: headers,
        },
      )
      .pipe(map((UserListAPI: UserListAPI) => UserList.factory(UserListAPI)));
  }

  constructor() {}
}
